import React, { Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, redirect } from 'react-router-dom';

// Constants
import { useSelector } from 'react-redux';
import { ROUTES } from './constants/routes';

// Layout & Components
import { LoadingScreen } from './screens/LoadingScreen';

import { dashboardRoutes, adminRoutes } from './layout/DashboardRouter';
import { store, type RootState } from './redux/store';
import { DashboardLayout } from './layout/DashboardLayout';
import { encryptStorage } from './encryptStorage';
import { authReducer } from './redux/slices/authReducer.slice';

// Core screens
const LoginScreen = React.lazy(() => import('./screens/LoginScreen').then(m => ({ default: m.LoginScreen })));
const ResetPasswordScreen = React.lazy(() =>
  import('./screens/ResetPasswordScreen').then(m => ({ default: m.ResetPasswordScreen })),
);
const ResetPasswordConfirmationScreen = React.lazy(() =>
  import('./screens/ResetPasswordConfirmScreen').then(m => ({ default: m.ResetPasswordConfirmationScreen })),
);
const VerifyEmailScreen = React.lazy(() =>
  import('./screens/VerifyEmailScreen').then(m => ({ default: m.VerifyEmailScreen })),
);
const ErrorScreen = React.lazy(() => import('./screens/ErrorScreen').then(m => ({ default: m.ErrorScreen })));

const ProtectedRoute = () => {
  return (
    <DashboardLayout>
      <Suspense fallback={<LoadingScreen />}>
        <Outlet />
      </Suspense>
    </DashboardLayout>
  );
};

const AdminRouteGuard = () => {
  const isSuperUser = useSelector((state: RootState) => state.authReducer.isSuperUser);

  if (!isSuperUser) {
    return <Navigate to={ROUTES.MAP_OVERVIEW} replace />;
  }
  return <Outlet />;
};

const requireAuth = () => {
  const token = encryptStorage.getItem('token');
  const refreshToken = encryptStorage.getItem('refresh_token');
  if (token) {
    store.dispatch(authReducer.actions.login({ token, refresh_token: refreshToken }));
    return true;
  }
  const currentPath = window.location.pathname;
  const searchParams = window.location.search;
  const fullPath = currentPath && searchParams ? `${currentPath}${searchParams}` : currentPath;
  throw redirect(`${ROUTES.LOGIN}${fullPath ? `?to=${encodeURIComponent(fullPath)}` : ''}`);
};
const router = createBrowserRouter([
  {
    path: ROUTES.LOGIN,
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <LoginScreen />
      </Suspense>
    ),
    errorElement: <ErrorScreen />,
  },
  {
    path: ROUTES.RESET_PASSWORD,
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <ResetPasswordScreen />
      </Suspense>
    ),
    errorElement: <ErrorScreen />,
  },
  {
    path: ROUTES.VERIFY_EMAIL,
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <VerifyEmailScreen />
      </Suspense>
    ),
    errorElement: <ErrorScreen />,
  },

  {
    path: ROUTES.RESET_PASSWORD_CONFIRMATION,
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <ResetPasswordConfirmationScreen />
      </Suspense>
    ),
    errorElement: <ErrorScreen />,
  },
  {
    element: <ProtectedRoute />,
    loader: requireAuth, // Add the loader
    children: [
      {
        element: <AdminRouteGuard />,
        children: adminRoutes.children,
      },
      ...dashboardRoutes.children,
    ],
  },
]);

export { router };
